import { Field } from '@/components';
import {
  FILE_SIZE_LIMITS,
  FILE_TYPE_CONFIG,
  fileAccept,
  TEXT_GRAY_500,
  TEXT_SECONDARY,
} from '@/constants';
import { useConfirm } from '@/hooks';
import { trans } from '@/i18n';
import { http } from '@/services/base/http';
import { FileType, FileTypeConfig, IBaseUploadRef } from '@/types';
import { showError } from '@/utils';
import { Box, Button, Flex, GridItem, Text } from '@chakra-ui/react';
import {
  ForwardedRef,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Control, Controller, FieldErrors, UseFormSetValue, UseFormTrigger } from 'react-hook-form';
import { BiCloudDownload, BiSolidCloudDownload } from 'react-icons/bi';
import { CiCloudOn } from 'react-icons/ci';

interface IProps {
  span?: number;
  name: string;
  label: string;
  disabled?: boolean;
  hideLabel?: boolean;
  placeholder?: string;
  control?: Control<any>;
  errors?: FieldErrors<any>;
  maxFiles?: number;
  accept?: string[];
  setValue: UseFormSetValue<any>;
  trigger: UseFormTrigger<any>;
  hideDescription?: boolean;
  size?: number;
  className?: string;
  description?: string;
  fileSize?: number;
  fileSizeName?: string;
  confirmChangeFile?: boolean;
  onChange?: () => void;
  fileType?: FileType;
  fileUrl?: string;
  fileName?: string;
}

interface FileValidationResult {
  isValid: boolean;
  errorMessage?: string;
}

function Component(props: IProps, ref: ForwardedRef<IBaseUploadRef>) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const { toggleConfirm } = useConfirm();
  const fileRef = useRef<HTMLInputElement>(null);

  const fileTypeConfig = useMemo((): FileTypeConfig | null => {
    if (!props.fileType) return null;
    return FILE_TYPE_CONFIG[props.fileType];
  }, [props.fileType]);

  const getFileTypeText = useCallback((): string => {
    if (selectedFile || props.fileUrl) {
      return trans('plan.fileUploadTexts.reUpload');
    }

    if (!fileTypeConfig) {
      return 'ここにファイルを';
    }

    return trans(fileTypeConfig.uploadTextKey);
  }, [selectedFile, props.fileUrl, fileTypeConfig]);

  const getMaxFileSize = useCallback((): number => {
    return fileTypeConfig?.maxSize ?? FILE_SIZE_LIMITS.DEFAULT;
  }, [fileTypeConfig]);

  const getFileTypeName = useCallback((): string => {
    return fileTypeConfig?.name ?? 'ファイル';
  }, [fileTypeConfig]);

  const getFileExtension = useCallback((): string => {
    return fileTypeConfig?.extension ?? '';
  }, [fileTypeConfig]);

  const validateFile = useCallback(
    (file: File): FileValidationResult => {
      if (!file) {
        return { isValid: false, errorMessage: 'No file provided' };
      }

      const maxSize = getMaxFileSize();
      if (file.size > maxSize) {
        const maxSizeMB = maxSize / (1024 * 1024);
        return {
          isValid: false,
          errorMessage: trans('plan.fileValidation.fileTooLarge', { maxSize: `${maxSizeMB}MB` }),
        };
      }

      const expectedExtension = getFileExtension();
      if (!expectedExtension) {
        return { isValid: true };
      }

      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      if (fileExtension !== expectedExtension) {
        const fileTypeName = getFileTypeName();
        return {
          isValid: false,
          errorMessage: trans('plan.fileValidation.invalidFileType', {
            fileType: fileTypeName,
            extension: expectedExtension,
          }),
        };
      }

      return { isValid: true };
    },
    [getMaxFileSize, getFileExtension, getFileTypeName]
  );

  const processFile = useCallback(
    (file: File): void => {
      const validation = validateFile(file);

      if (!validation.isValid) {
        showError(validation.errorMessage!);
        return;
      }

      setSelectedFile(file);
      props.setValue(props.name, file);
      props.trigger(props.name);
      props.onChange?.();
    },
    [
      validateFile,
      props.fileType,
      props.name,
      props.control?._formValues?.htmlFile,
      props.setValue,
      props.trigger,
      props.onChange,
    ]
  );

  const handleFileSelect = useCallback(
    (file: File): void => {
      if (!file) return;
      processFile(file);
    },
    [processFile]
  );

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const file = event.target.files?.[0];
      if (!file) return;

      const validation = validateFile(file);
      if (!validation.isValid) {
        showError(validation.errorMessage!);
        if (event.target) {
          event.target.value = '';
        }
        return;
      }

      if (selectedFile) {
        toggleConfirm({
          title: 'データが上書きされます',
          message: '上書きすると復元できませんがよろしいでしょうか？',
          okText: 'はい',
          cancelText: 'いいえ',
          onOk: () => handleFileSelect(file),
        });
      } else {
        handleFileSelect(file);
      }
    },
    [validateFile, selectedFile, toggleConfirm, handleFileSelect]
  );

  const handleDrag = useCallback((e: React.DragEvent): void => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent): void => {
      e.preventDefault();
      e.stopPropagation();

      const file = e.dataTransfer.files?.[0];
      if (!file) return;

      const validation = validateFile(file);
      if (!validation.isValid) {
        showError(validation.errorMessage!);
        return;
      }

      if (selectedFile) {
        toggleConfirm({
          title: 'データが上書きされます',
          message: '上書きすると復元できませんがよろしいでしょうか？',
          okText: 'はい',
          cancelText: 'いいえ',
          onOk: () => handleFileSelect(file),
        });
      } else {
        handleFileSelect(file);
      }
    },
    [validateFile, selectedFile, toggleConfirm, handleFileSelect]
  );

  const handleDownload = useCallback(async (): Promise<void> => {
    try {
      if (selectedFile) {
        const url = URL.createObjectURL(selectedFile);
        const a = document.createElement('a');
        a.href = url;
        a.download = selectedFile.name;
        a.dataset.skipGuard = 'true';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else if (props.fileUrl) {
        // Download via API with auth and force filename
        const response = await http.get(props.fileUrl, { responseType: 'blob' });
        const blob = new Blob([response.data], {
          type: response.headers['content-type'] || 'application/octet-stream',
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = props.fileName || 'download';
        a.dataset.skipGuard = 'true';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      showError(error);
    }
  }, [selectedFile, props.fileUrl, props.fileName]);

  const handleFileUploadClick = useCallback((): void => {
    fileRef.current?.click();
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      openFile: handleFileUploadClick,
    }),
    [handleFileUploadClick]
  );

  const fileInfoDisplay = useMemo(() => {
    const displayFileName = selectedFile?.name || props.fileName;
    if (!selectedFile && !props.fileUrl) return null;

    return (
      <Flex
        align="center"
        justify="center"
        gap={2}
        border="1px solid #4FD1C5"
        borderRadius="15px"
        w="140px"
        minW="140px"
        h="27px"
      >
        <Text
          w="76px"
          fontSize="14px"
          fontWeight="400"
          color={TEXT_GRAY_500}
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
          pl="10px"
        >
          {displayFileName}
        </Text>
        <Button
          variant="ghost"

          minW="auto"
          h="auto"
          p={1}
          position="relative"
          _hover={{ bg: 'transparent' }}
          className="download-button"
        >
          <BiCloudDownload
            fontSize="24px"
            color={TEXT_SECONDARY}
            className="download-icon-default"
          />
          <BiSolidCloudDownload onClick={handleDownload} fontSize="24px" color="#6A6A6A" className="download-icon-hover" />
        </Button>
      </Flex>
    );
  }, [selectedFile, props.fileUrl, props.fileName, handleDownload]);

  const uploadZoneContent = useMemo(
    () => (
      <Flex direction="row" align="center" justify="center" gap="12px">
        <CiCloudOn fontSize="18.66px" color={TEXT_SECONDARY} />
        <Box display="flex" flexDirection="column" gap="4px" textAlign="left">
          <Text fontSize="11.66px" color={TEXT_SECONDARY} fontWeight="400">
            {getFileTypeText()}
          </Text>
          {!selectedFile && !props.fileUrl && (
            <Text fontSize="11.66px" color={TEXT_SECONDARY} fontWeight="400">
              {trans('plan.fileUploadTexts.dropAndUpload')}
            </Text>
          )}
        </Box>
      </Flex>
    ),
    [getFileTypeText, selectedFile, props.fileUrl]
  );

  return (
    <GridItem className={props.className} colSpan={props.span || 6}>
      <Controller
        control={props.control}
        rules={{
          required: true,
        }}
        render={() => (
          <Field
            label={
              !props.hideLabel ? (
                <Flex
                  align="end"
                  justify="space-between"
                  gap="10px"
                  width="100%"
                  height="20px"
                  px="4px"
                >
                  <Text w="100%">{props.label}</Text>
                  {fileInfoDisplay}
                </Flex>
              ) : undefined
            }
            invalid={!!props.errors?.[props.name]}
            errorText={props.errors?.[props.name]?.message}
          >
            <Box
              className="custom-file-upload-dropzone"
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={handleFileUploadClick}
            >
              {uploadZoneContent}
              <input
                ref={fileRef}
                type="file"
                accept={props.accept?.join(',') || fileAccept.texture.join(',')}
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
            </Box>
          </Field>
        )}
        name={props.name}
      />
    </GridItem>
  );
}

export const BaseFileUpload = forwardRef(Component);
